/**
 * Pure model for the Human Call tab's "All fields" board (right pane).
 *
 * {@link buildFieldBoard} enumerates EVERY output leaf field (top-level scalars +
 * entity-instance/nested) with its live {@link FieldStatus}, grouped by schema group.
 * The recursive flatten is delegated to {@link walkTemplateToLeaves} so the board and
 * the guided script share one keying/nesting rule; each leaf carries its entity
 * `instance` + `section` so the board can render nested sub-groups (right pane).
 *
 * This module is PURE (no React, no I/O) so it can be unit-tested in isolation.
 */
import type { FieldGroup, FormField } from '@/types/formSchema';

import type { FieldStatus } from './fieldStatus';
import { isEmptyValue } from './fieldStatus';
import type { LeafInstance, StatusCtx, TreeLeaf } from './fieldTree';
import { ENTITY_INPUT_KEY, walkTemplateToLeaves } from './fieldTree';

export interface BoardField {
  key: string;
  label: string;
  type: FormField['type'];
  status: FieldStatus;
  value: unknown;
  /** Schema field def — used by the board for inline editing. */
  field: FormField;
  /** Entity instance this leaf lives under (e.g. `drug[0]` + code label); undefined for top-level. */
  instance?: LeafInstance;
  /** Nearest enclosing nested-object label ("Coverage"); undefined for a direct leaf. */
  section?: string;
  /** Legacy compact sub-group label (section, else instance label). Prefer `instance`/`section`. */
  sub?: string;
  required: boolean;
}

export interface BoardSection {
  title: string;
  fields: BoardField[];
}

/** A run of fields sharing a section header within one instance (undefined = no header). */
interface BoardSubSection {
  section: string | undefined;
  fields: BoardField[];
}

/** Board fields grouped by entity instance (undefined = top-level), each split into sections. */
export interface BoardInstanceGroup {
  instance?: LeafInstance;
  sections: BoardSubSection[];
}

/**
 * Group already-filtered board rows into the right pane's nested render shape:
 * entity instance (undefined = top-level) → section sub-group → fields. Instance runs stay
 * consecutive (the walk emits an instance's fields contiguously), but sections are merged by
 * label WITHIN each instance — so a scalar before AND after a nested sub-object (both carrying
 * the parent's label) land in one section group, keeping section labels unique for safe React
 * keys (no duplicate-key mis-reconcile). PURE.
 */
export function groupBoardRows(fields: BoardField[]): BoardInstanceGroup[] {
  const groups: BoardInstanceGroup[] = [];
  for (const field of fields) {
    let group = groups[groups.length - 1];
    if (!group || group.instance?.key !== field.instance?.key) {
      group = { instance: field.instance, sections: [] };
      groups.push(group);
    }
    const existing = group.sections.find(s => s.section === field.section);
    if (existing) {
      existing.fields.push(field);
    } else {
      group.sections.push({ section: field.section, fields: [field] });
    }
  }
  return groups;
}

/** One code's value for a {@link BoardCodeRow}. */
interface BoardCodeCell {
  instance: LeafInstance;
  field: BoardField;
}

/** One field of a multi-code entity, with a cell per code (`coverage.copay` → J1234, J1235). */
export interface BoardCodeRow {
  /** Path inside the entity element, e.g. `coverage.copay`. */
  rowKey: string;
  label: string;
  cells: BoardCodeCell[];
}

/**
 * A run of board rows in render order (BV-2742): either an ordinary instance group, or a
 * multi-code entity collapsed to one row per field with a chip per code.
 */
type BoardRowBlock =
  | { kind: 'fields'; group: BoardInstanceGroup }
  | {
      kind: 'codes';
      /** Unindexed entity key, e.g. `drug`. */
      entity: string;
      sections: { section: string | undefined; rows: BoardCodeRow[] }[];
    };

/** `drug[2]` → `drug`. Nested arrays keep their parent index (`drug[0].pbm`). */
function entityOf(instance: LeafInstance): string {
  return instance.key.replace(/\[\d+\]$/, '');
}

/** Entities with 2+ instances among `fields` — the ones the board shows as code chips. */
export function multiCodeEntities(fields: BoardField[]): Set<string> {
  const instances = new Map<string, Set<string>>();
  for (const f of fields) {
    if (!f.instance) continue;
    const entity = entityOf(f.instance);
    const keys = instances.get(entity) ?? new Set<string>();
    keys.add(f.instance.key);
    instances.set(entity, keys);
  }
  return new Set([...instances].filter(([, keys]) => keys.size > 1).map(([entity]) => entity));
}

/** Collapse consecutive instance groups of one entity into rows with a cell per code. */
function toCodeBlock(entity: string, groups: BoardInstanceGroup[]): BoardRowBlock {
  const sections: { section: string | undefined; rows: BoardCodeRow[] }[] = [];
  const rows = new Map<string, BoardCodeRow>();
  for (const group of groups) {
    const instance = group.instance as LeafInstance;
    for (const sub of group.sections) {
      for (const field of sub.fields) {
        const rowKey = field.key.slice(instance.key.length + 1);
        let row = rows.get(rowKey);
        if (!row) {
          row = { rowKey, label: field.label, cells: [] };
          rows.set(rowKey, row);
          let target = sections.find(s => s.section === sub.section);
          if (!target) {
            target = { section: sub.section, rows: [] };
            sections.push(target);
          }
          target.rows.push(row);
        }
        row.cells.push({ instance, field });
      }
    }
  }
  return { kind: 'codes', entity, sections };
}

/**
 * {@link groupBoardRows}, with each multi-code entity collapsed into one row per field and
 * a cell per code (BV-2742). `multi` is decided on the UNFILTERED section (see
 * {@link multiCodeEntities}), so narrowing to Missed never flips a two-code entity back to
 * per-code groups just because one code has nothing left to show. PURE.
 */
export function groupBoardBlocks(fields: BoardField[], multi: Set<string>): BoardRowBlock[] {
  const blocks: BoardRowBlock[] = [];
  let run: { entity: string; groups: BoardInstanceGroup[] } | undefined;
  const flush = () => {
    if (run) blocks.push(toCodeBlock(run.entity, run.groups));
    run = undefined;
  };
  for (const group of groupBoardRows(fields)) {
    const entity = group.instance ? entityOf(group.instance) : undefined;
    if (entity && multi.has(entity)) {
      if (run?.entity !== entity) flush();
      run ??= { entity, groups: [] };
      run.groups.push(group);
      continue;
    }
    flush();
    blocks.push({ kind: 'fields', group });
  }
  flush();
  return blocks;
}

function leafToBoardField(leaf: TreeLeaf): BoardField {
  return {
    key: leaf.key,
    label: leaf.label,
    type: leaf.type,
    status: leaf.status,
    value: leaf.value,
    field: leaf.field,
    required: leaf.required,
    instance: leaf.instance,
    section: leaf.section,
    sub: leaf.section ?? leaf.instance?.label,
  };
}

/**
 * The effective value for a schema field before walking. Top-level ENTITY arrays
 * (`drug`/`procedure`/`sites_of_care`) are seeded from the input billable-code arrays so
 * their fields surface for capture even when nothing has been captured yet — mirroring the
 * script's `buildEntityUnits` fan-out (without seeding, an all-empty entity group produces
 * zero leaves and vanishes from the board — BI-1217). Non-array fields pass through.
 *
 * Per instance: keep the captured output element and its own `code`/`name`; only when those
 * are absent, borrow the positional input element's `code`/`name` (header + code leaf) — so
 * an output element's own code is never overwritten (no PHI mislabel under skew).
 */
/** Output element enriched with the input's `code`/`name` only where the output lacks them. */
function mergeInstance(out: unknown, inp: unknown): Record<string, unknown> {
  const o = (out ?? {}) as Record<string, unknown>;
  const i = (inp ?? {}) as Record<string, unknown>;
  const el: Record<string, unknown> = { ...o };
  if (isEmptyValue(el.code) && !isEmptyValue(i.code)) el.code = i.code;
  if (isEmptyValue(el.name) && !isEmptyValue(i.name)) el.name = i.name;
  return el;
}

function seedEntityValue(
  field: FormField,
  outputValue: unknown,
  inputData: Record<string, unknown>,
): unknown {
  // Only the known fan-out entities seed from an input code array. A generic array field
  // (not in ENTITY_INPUT_KEY) must NOT borrow from a same-named input array.
  const inputKey = ENTITY_INPUT_KEY[field.id];
  if (field.type !== 'array' || !inputKey) return outputValue;
  const outArr = Array.isArray(outputValue) ? outputValue : [];
  const inputArrRaw = inputData[inputKey];
  const inArr = Array.isArray(inputArrRaw) ? inputArrRaw : [];
  const count = Math.max(outArr.length, inArr.length);
  if (count === 0) return outArr;
  // Merge positionally at EVERY index (not only the input-longer tail) so an output element
  // lacking its own code borrows the positional input code — matching the script model's
  // `outElem.code ?? inElem.code`, so a drug labels identically on the board and the script.
  return Array.from({ length: count }, (_, i) => mergeInstance(outArr[i], inArr[i]));
}

/**
 * Enumerate EVERY output leaf field (top-level scalars + entity-instance/nested) with its
 * live status, grouped by schema group — the data for the right "All fields" board.
 * Entity arrays are seeded from `inputData` (see {@link seedEntityValue}) so their fields
 * appear before capture. PURE (no React/IO); unit-tested.
 */
export function buildFieldBoard(args: {
  outputSchemaGroups: FieldGroup[];
  outputData: Record<string, unknown>;
  /** Case input data — seeds entity instances (billable codes) so empty entities still show. */
  inputData?: Record<string, unknown>;
  missingFields: string[];
  requiredFields?: string[];
  heardKeys: Set<string>;
  sessionEditedKeys: Set<string>;
}): BoardSection[] {
  const ctx: StatusCtx = {
    missing: new Set(args.missingFields),
    required: new Set(args.requiredFields ?? args.missingFields),
    heard: args.heardKeys,
    edited: args.sessionEditedKeys,
  };
  const inputData = args.inputData ?? {};
  return args.outputSchemaGroups.map(group => ({
    title: group.label,
    fields: group.fields.flatMap(f =>
      walkTemplateToLeaves(f, f.id, seedEntityValue(f, args.outputData[f.id], inputData), ctx).map(
        leafToBoardField,
      ),
    ),
  }));
}
